import { routes } from '../../../routes'; // Update the path based on your project structure
import { Flex, Spin, Button as AntButton, App } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as Back } from '../../../assets/arrow.svg';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createUpdateEmailTemplate, getEmailTemplateById } from './request';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  Alignment,
  Bold,
  ClassicEditor,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  GeneralHtmlSupport,
  Heading,
  HtmlComment,
  HtmlEmbed,
  Italic,
  Link,
  List,
  Mention,
  Paragraph,
  SourceEditing,
  Style,
  Table,
  TableCellProperties,
  TableProperties,
  TableToolbar,
  Underline,
  Undo,
} from 'ckeditor5';
import { useRef, useState, type ComponentType } from 'react';
import Button from '../../../custom/button/button';

const CKEditorComponent = CKEditor as unknown as ComponentType<any>;

const ViewTemplate = () => {
  const params = useParams();
  const { notification } = App.useApp();
  const [changes, setChanges] = useState('');
  const navigate = useNavigate();
  const templateRef = useRef<any>(null);

  const onReady = (editor: any) => {
    templateRef.current = editor;
  };
  const id = params.id;

  const updateTemplateMutation = useMutation({ mutationFn: (payload: FormData) => createUpdateEmailTemplate(payload) });
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['emailTemplate', id],
    queryFn: () => getEmailTemplateById(Number(id)),
  });

  const template = data?.data as EmailTemplate;

  const handleUpdate = async () => {
    try {
      const formData = new FormData();
      if (id) {
        formData.append('Id', id);
      }
      formData.append('Code', template?.code);
      formData.append('Body', changes || template?.body || '');
      formData.append('ActiveStatus', String(template?.activeStatus || true));
      await updateTemplateMutation.mutateAsync(formData, {
        onSuccess: (data) => {
          notification.success({
            message: 'Success',
            description: data?.message || 'Template updated successfully',
          });
          refetch();
        },
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || 'An error occurred while updating template',
      });
    }
  };

  if (isLoading) {
    return <Spin />;
  }

  return (
    <div>
      <Flex align="center" gap="8px">
        <AntButton onClick={() => navigate(routes.userMgt.emailTemplates)} variant="text">
          <Back />
        </AntButton>
        <h3>{template?.code}</h3>
      </Flex>
      <br />
      <div className="fields">
        <h4>Editor</h4>
        <CKEditorComponent
          editor={ClassicEditor}
          config={{
            toolbar: {
              shouldNotGroupWhenFull: true,
              items: [
                'heading',
                'undo',
                'redo',
                '|',
                'bold',
                'italic',
                'underline',
                'fontFamily',
                'fontSize',
                'fontColor',
                'fontBackgroundColor',
                'alignment',
                'link',
                '|',
                'numberedList',
                'bulletedList',
                '|',
                'insertTable',
                'sourceEditing',
                'htmlEmbed',
              ],
            },
            plugins: [
              Essentials,
              Paragraph,
              Heading,
              Bold,
              Italic,
              Underline,
              Link,
              List,
              Mention,
              Undo,
              Alignment,
              FontFamily,
              FontSize,
              FontColor,
              FontBackgroundColor,
              Table,
              TableToolbar,
              TableProperties,
              TableCellProperties,
              GeneralHtmlSupport,
              SourceEditing,
              HtmlEmbed,
              HtmlComment,
              Style,
            ],
            table: {
              contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties'],
            },
            htmlSupport: {
              allow: [
                {
                  name: /.*/,
                  attributes: true,
                  classes: true,
                  styles: true,
                },
              ],
            },
            initialData: template?.body,
          }}
          onChange={(_: any, editor: { getData: () => any; }) => {
            const data = editor.getData();
            setChanges(data);
          }}
          onReady={onReady}
          data={template?.body || ''}
        />
        <br />
        <h4>Preview</h4>
        <div
          style={{
            border: '1px solid #ddd',
            padding: '16px',
            borderRadius: '4px',
            backgroundColor: '#fafafa',
            minHeight: '200px',
          }}
          dangerouslySetInnerHTML={{
            __html: changes || template?.body || '<p>No content</p>',
          }}
        />
        <br />
        <Button text="Save Changes" onClick={handleUpdate} isLoading={updateTemplateMutation.isPending} />
      </div>
    </div>
  );
};

export default ViewTemplate;
